import { uiEffectsEnabled } from "../../graphics/graphics_quality.js";
import { TransformOptions, UIElement } from "./ui_element.js";

export interface DiagonalStripesOverlayOptions
{
  color?: string;
  opacity?: number;
  stripe_width?: number;
  gap_width?: number;
  angle_deg?: number;
  speed?: number;
  corner_radius?: number;
}

export class DiagonalStripesOverlay extends UIElement
{
  private color: string;
  private stripe_opacity: number;
  private stripe_width: number;
  private gap_width: number;
  private angle_rad: number;
  private speed: number;
  private corner_radius: number;

  private offset = 0;
  private last_time = 0;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    options: DiagonalStripesOverlayOptions = {},
    ui_options?: TransformOptions
  )
  {
    super( ctx, x, y, width, height, false, false, ui_options );

    this.color = options.color ?? "#FFFFFF";
    this.stripe_opacity = options.opacity ?? 0.35;
    this.stripe_width = options.stripe_width ?? 16;
    this.gap_width = options.gap_width ?? 16;
    this.angle_rad = ( options.angle_deg ?? 22 ) * Math.PI / 180;
    this.speed = options.speed ?? 60;
    this.corner_radius = options.corner_radius ?? 0;
  }

  public setSize( width: number, height: number ): void
  {
    this.rect.resize( width, height );
    this.setShouldBeRendered();
  }

  public setCornerRadius( radius: number ): void
  {
    this.corner_radius = radius;
  }

  protected updateBasic( now: number ): void
  {
    super.updateBasic( now );

    if ( this.last_time === 0 )
      this.last_time = now;

    const dt = ( now - this.last_time ) / 1000;
    this.last_time = now;

    const period = this.stripe_width + this.gap_width;
    if ( period > 0 )
      this.offset = ( this.offset + dt * this.speed ) % period;

    if ( !this.dom_mode )
      this.setShouldBeRendered();
  }

  private dom_drift: Animation | null = null;
  private dom_drift_key = "";
  private dom_stripes: HTMLDivElement | null = null;

  private domStripesLayer( node: HTMLElement ): HTMLDivElement
  {
    if ( !this.dom_stripes )
    {
      const layer = document.createElement( "div" );
      layer.className = "ui-dom-stripes";
      const s = layer.style;
      s.position = "absolute";
      s.top = "0";
      s.pointerEvents = "none";
      s.willChange = "transform";
      node.appendChild( layer );
      this.dom_stripes = layer;
    }
    return this.dom_stripes;
  }

  private syncDomDrift( layer: HTMLElement, period: number ): void
  {
    const key = `${period}|${this.speed}`;
    if ( key === this.dom_drift_key )
      return;
    this.dom_drift_key = key;
    this.dom_drift?.cancel();
    this.dom_drift = null;
    if ( period <= 0 || this.speed === 0 || !uiEffectsEnabled() )
      return;
    const duration = Math.abs( period / this.speed ) * 1000;
    const to = this.speed > 0 ? period : -period;
    try
    {
      this.dom_drift = layer.animate(
        [ { translate: "0px 0px", easing: "linear" }, { translate: `${to}px 0px` } ],
        { duration, iterations: Infinity }
      );
    }
    catch { this.dom_drift = null; }
  }

  public override cleanup(): void
  {
    this.dom_drift?.cancel();
    this.dom_drift = null;
    super.cleanup();
  }

  private clipToRoundedRect( w: number, h: number, r: number ): void
  {
    const w2 = w / 2;
    const h2 = h / 2;
    const radius = Math.max( 0, Math.min( r, w2, h2 ) );

    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo( -w2 + radius, -h2 );
    ctx.lineTo(  w2 - radius, -h2 );
    ctx.arcTo(  w2, -h2,  w2, -h2 + radius, radius );
    ctx.lineTo(  w2,  h2 - radius );
    ctx.arcTo(  w2,  h2,  w2 - radius,  h2, radius );
    ctx.lineTo( -w2 + radius,  h2 );
    ctx.arcTo( -w2,  h2, -w2,  h2 - radius, radius );
    ctx.lineTo( -w2, -h2 + radius );
    ctx.arcTo( -w2, -h2, -w2 + radius, -h2, radius );
    ctx.closePath();
    ctx.clip();
  }

  private dom_last_key = "";

  protected override syncDomContent( node: HTMLElement ): boolean
  {
    const w = this.rect.getW();
    const h = this.rect.getH();
    const period = this.stripe_width + this.gap_width;
    const cos = Math.cos( this.angle_rad );
    const p = period * cos;
    const sw = this.stripe_width * cos;
    const deg = 90 + this.angle_rad * 180 / Math.PI;
    const radius = Math.max( 0, Math.min( this.corner_radius, w / 2, h / 2 ) );
    const stops = `${this.color} 0px, ${this.color} ${sw}px, transparent ${sw}px, transparent ${p}px`;
    const background = period > 0 ? `repeating-linear-gradient(${deg}deg, ${stops}) 0px 0px / ${period}px 100% repeat` : "";
    const layer = this.domStripesLayer( node );
    this.syncDomDrift( layer, period );
    const key = `${background}|${radius}|${this.stripe_opacity}|${w}|${h}`;
    if ( key === this.dom_last_key )
      return true;
    this.dom_last_key = key;
    const s = node.style;
    s.borderRadius = radius > 0 ? `${radius}px` : "";
    s.overflow = "hidden";
    s.filter = `opacity(${this.stripe_opacity})`;
    const l = layer.style;
    l.left = `${-period}px`;
    l.width = `${w + period * 2}px`;
    l.height = `${h}px`;
    l.background = background;
    return true;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    const w = this.rect.getW();
    const h = this.rect.getH();

    if ( w > 0 && h > 0 && !this.dom_mode )
    {
      const ctx = this.ctx;
      ctx.save();

      this.clipToRoundedRect( w, h, this.corner_radius );

      ctx.globalAlpha *= this.stripe_opacity;
      ctx.fillStyle = this.color;

      const period = this.stripe_width + this.gap_width;
      const skew = Math.tan( this.angle_rad ) * h;
      const w2 = w / 2;
      const h2 = h / 2;

      ctx.beginPath();
      if ( period > 0 )
      {
        for ( let sx = -w2 - skew - period + this.offset; sx < w2 + skew + period; sx += period )
        {
          ctx.moveTo( sx, h2 );
          ctx.lineTo( sx + skew, -h2 );
          ctx.lineTo( sx + skew + this.stripe_width, -h2 );
          ctx.lineTo( sx + this.stripe_width, h2 );
          ctx.closePath();
        }
      }
      ctx.fill();

      ctx.restore();
    }

    this.finishRender();
  }

  public name(): string
  {
    return "DiagonalStripesOverlay";
  }
}
